import { SingleTopUpModule } from './single'
import { TopUpModule as ParentTopUpModule } from '../index'
import gql from 'graphql-tag'
import { getProvider } from '@/apollo'
import { splitError } from '@/helper/splitError'

const initialState = {
  changeTopUp: {
    view: 'default',
    topUpData: null,
    activationView: 'activation',
    deactivationView: 'deactivation',
    success: false
  },
  voucherTopUp: {
    view: 'input',
    amount: 0,
    voucherCode: null
  }
}

const TopUpModule = {
  namespaced: true,
  modules: {
    single: SingleTopUpModule
  },
  state: initialState,
  actions: {
    async deactivateAutoTopUp ({ commit, state }, nextStep) {
      commit('clearMessages', {}, { root: true })

      const apolloClient = getProvider().defaultClient
      return apolloClient.mutate({
        mutation: gql`mutation changeAutoTopUp {
          deactivateAutoTopUp{
            activationPossible,
            active,
            topUp {
              amount,
              trigger,
              type
            }
          }
        }`
      }).then((response) => {
        state.changeTopUp.deactivationView = nextStep
        state.changeTopUp.success = true
        commit('Transaction/removeTransactionId', 'changeAutoTopUp', { root: true })
      }).catch((error) => {
        splitError(error).forEach(e => commit('addError', e, { root: true }))
      })
    },
    async changeAutoTopUp ({ commit, state, getters }, { topUpData, amount, nextStep }) {
      commit('clearMessages', {}, { root: true })

      const apolloClient = getProvider().defaultClient
      return apolloClient.mutate({
        mutation: gql`mutation changeAutoTopUp ($changeAutoTopUpInput: ChangeAutoTopUpInput!) {
          changeAutoTopUp (autoTopUp: $changeAutoTopUpInput){
            activationPossible,
            active,
            topUp {
              amount,
              trigger,
              type
            }
          }
        }`,
        variables: {
          changeAutoTopUpInput: {
            amount: amount,
            trigger: topUpData.trigger,
            type: getters.selectType(topUpData.type)
          }
        }
      }).then((response) => {
        state.changeTopUp.activationView = nextStep
        state.changeTopUp.success = true
        state.changeTopUp.topUpData = response.data.changeAutoTopUp
        commit('Transaction/removeTransactionId', 'changeAutoTopUp', { root: true })
      }).catch((error) => {
        splitError(error).forEach(e => commit('addError', e, { root: true }))
      })
    },
    async confirmAutoTopUp ({ commit, state, getters }, nextStep) {
      commit('clearMessages', {}, { root: true })

      const apolloClient = getProvider().defaultClient
      return apolloClient.mutate({
        mutation: gql`mutation changeAutoTopUp ($changeAutoTopUpInput: ChangeAutoTopUpInput!) {
          changeAutoTopUp (autoTopUp: $changeAutoTopUpInput){
            activationPossible,
            active,
            topUp {
              amount,
              trigger,
              type
            }
          }
        }`,
        variables: {
          changeAutoTopUpInput: {
            amount: state.changeTopUp.topUpData.amount,
            trigger: state.changeTopUp.topUpData.trigger,
            type: getters.selectType(state.changeTopUp.topUpData.type)
          }
        }
      }).then((response) => {
        commit('setActivationView', nextStep)
        commit('Transaction/removeTransactionId', 'changeAutoTopUp', { root: true })
        if (getters.selectedType === 'byLimit') {
          commit('addMessage', { type: 'success', message: 'Die automatische Aufladung wurde erfolgreich eingerichtet.' }, { root: true })
        } else {
          commit('addMessage', { type: 'success', message: 'Die monatliche Aufladung wurde erfolgreich eingerichtet.' }, { root: true })
        }
      }).catch((error) => {
        commit('setActivationView', nextStep)
        splitError(error).forEach(e => commit('addError', e, { root: true }))
      })
    },
    async sendVoucher ({ commit, state, rootGetters }, { voucher, nextStep }) {
      commit('clearMessages', {}, { root: true })
      const apolloClient = getProvider().defaultClient
      return apolloClient.mutate({
        mutation: gql`mutation voucher ($input: String!) {
          topUpVoucher (voucher: $input) {
            amount
          }
        }`,
        variables: {
          input: voucher
        }
      }).then((response) => {
        commit('storeVoucherCode', voucher)
        commit('setVoucherView', nextStep)
        commit('Transaction/removeTransactionId', 'voucher', { root: true })
      }).catch((error) => {
        splitError(error).forEach(e => commit('addError', e, { root: true }))
      })
    }
  },
  mutations: {
    returnToInput: ParentTopUpModule.mutations.returnToInput,
    setActivationView: ParentTopUpModule.mutations.setActivationView,
    setDeactivationView: ParentTopUpModule.mutations.setDeactivationView,
    storeTopUpData: (state, topUpData) => {
      state.changeTopUp.topUpData = topUpData
    },
    // Voucher
    storeVoucherCode: (state, voucherCode) => {
      state.voucherTopUp.voucherCode = voucherCode
    },
    setVoucherView: (state, nextStep) => {
      state.voucherTopUp.view = nextStep
    },
    setCurrentView: (state, nextView) => {
      state.changeTopUp.view = nextView
    },
    resetSuccess: (state, value) => {
      state.changeTopUp.success = value
    }
  },
  getters: {
    selectType: ParentTopUpModule.getters.selectType,
    view: ParentTopUpModule.getters.view,
    amount: ParentTopUpModule.getters.amount,
    getActivationView: ParentTopUpModule.getters.getActivationView,
    getDeactivationView: ParentTopUpModule.getters.getDeactivationView,
    selectedType (state) {
      return state.changeTopUp?.topUpData?.type || null
    },
    selectedAmount (state) {
      return state.changeTopUp?.topUpData?.amount || null
    },
    selectedTrigger (state) {
      return state.changeTopUp?.topUpData?.trigger || null
    },
    voucherCode: state => {
      return state.voucherTopUp?.voucherCode || null
    },
    currentView: state => {
      return state.changeTopUp.view
    },
    isSuccess: state => {
      return state.changeTopUp.success
    },
    topUpData (state) {
      return state.changeTopUp.topUpData
    }
  }
}

export {
  TopUpModule
}
